// categorySlice.js

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit" // import redux toolkit helpers
import { getCategories, getCategoryProducts } from "../../api/category" // import category api functions
import { addProducts } from "./productSlice" // import action to store normalized products



// ===============================
// INITIAL STATE
// ===============================
const initialState = {

  // categories stored by slug
  categories: {},

  loading: false,

  error: null

}



// ===============================
// FETCH CATEGORY STRUCTURE
// ===============================
export const fetchCategories = createAsyncThunk(

  "category/fetchCategories",

  async (_, thunkAPI) => {

    try {

      const res = await getCategories() // call category list api

      return res.data.data.categories || [] // return category array

    } catch (err) {

      return thunkAPI.rejectWithValue(err.response?.data) // return api error payload

    }

  }

)



// ===============================
// FETCH CATEGORY PRODUCTS
// ===============================
export const fetchCategoryProducts = createAsyncThunk(

  "category/fetchCategoryProducts",

  async ({ categorySlug, subcategorySlug }, thunkAPI) => {

    try {

      const res = await getCategoryProducts(categorySlug, subcategorySlug) // call api for category or subcategory products

      const products = res.data.data.products || [] // extract products array safely

      thunkAPI.dispatch(addProducts(products)) // store full products in product slice

      return {
        categorySlug,
        subcategorySlug,
        products
      } // return only relation data for category slice

    } catch (err) {

      return thunkAPI.rejectWithValue(err.response?.data) // return api error payload

    }

  }

)



// ===============================
// CATEGORY SLICE
// ===============================
const categorySlice = createSlice({

  name: "category",

  initialState,

  reducers: {

    clearCategoryError: (state) => {
      state.error = null // clear category error
    }

  },

  extraReducers: (builder) => {

    builder



      // ===============================
      // FETCH CATEGORY STRUCTURE - PENDING
      // ===============================
      .addCase(fetchCategories.pending, (state) => {

        state.loading = true // start loading
        state.error = null // reset error

      })



      // ===============================
      // STORE CATEGORY STRUCTURE
      // ===============================
      .addCase(fetchCategories.fulfilled, (state, action) => {

        state.loading = false // stop loading

        const categories = action.payload || [] // get categories safely

        categories.forEach(cat => {

          const slug = cat.slug // get category slug

          state.categories[slug] = {

            ...state.categories[slug], // preserve already stored data if present
            ...cat, // store api category fields
            product_ids: state.categories[slug]?.product_ids || [], // preserve existing category product ids
            subcategories: state.categories[slug]?.subcategories || {} // preserve existing subcategories if already built

          } // create or update category entry

          if (Array.isArray(cat.subcategories)) {

            cat.subcategories.forEach(sub => {

              state.categories[slug].subcategories[sub.slug] = {

                ...state.categories[slug].subcategories?.[sub.slug], // preserve old subcategory data
                ...sub, // store subcategory fields
                product_ids: state.categories[slug].subcategories?.[sub.slug]?.product_ids || [] // preserve subcategory product ids

              } // create or update subcategory entry

            })

          }

        })

      })



      // ===============================
      // FETCH CATEGORY STRUCTURE - REJECTED
      // ===============================
      .addCase(fetchCategories.rejected, (state, action) => {

        state.loading = false // stop loading
        state.error = action.payload?.message || "Failed to load categories" // store error message

      })



      // ===============================
      // FETCH CATEGORY PRODUCTS - PENDING
      // ===============================
      .addCase(fetchCategoryProducts.pending, (state) => {

        state.loading = true // start loading
        state.error = null // reset error

      })



      // ===============================
      // STORE CATEGORY PRODUCTS
      // ===============================
      .addCase(fetchCategoryProducts.fulfilled, (state, action) => {

        state.loading = false // stop loading

        const { categorySlug, subcategorySlug, products } = action.payload // extract payload data

        // create category if missing
        if (!state.categories[categorySlug]) {

          state.categories[categorySlug] = {
            slug: categorySlug, // store category slug
            product_ids: [], // initialize category product ids
            subcategories: {} // initialize subcategory map
          }

        }

        const category = state.categories[categorySlug] // get current category entry

        if (subcategorySlug) {

          // create subcategory if missing
          if (!category.subcategories[subcategorySlug]) {

            category.subcategories[subcategorySlug] = {
              slug: subcategorySlug, // store subcategory slug
              product_ids: [] // initialize subcategory product ids
            }

          }

          category.subcategories[subcategorySlug].product_ids = products.map(product => product.id || product.public_id) // replace subcategory ids with fresh ids

        } else {

          category.product_ids = products.map(product => product.id || product.public_id) // replace category ids with fresh ids

        }

      })



      // ===============================
      // FETCH CATEGORY PRODUCTS - REJECTED
      // ===============================
      .addCase(fetchCategoryProducts.rejected, (state, action) => {

        state.loading = false // stop loading
        state.error = action.payload?.message || "Failed to load products" // store error message

      })

  }

})



// ===============================
// EXPORTS
// ===============================
export const { clearCategoryError } = categorySlice.actions // export actions

export default categorySlice.reducer // export reducer